import { useState } from 'react'
import type { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { UnifiedCardSearch } from '../cards/UnifiedCardSearch'
import type { CardSearchResult, InventorySearchResult } from '../cards/UnifiedCardSearch'
import { api } from '../../utils/api'
import { getVariantPriceCents, pickBestPriceCents } from '../../utils/price'
import { getQuantityRangeWarning, normalizeQuantityDraft, parseQuantityDraft } from '../../utils/quantityInput'
import { CardPricingInsightsModal, type CardPricingModalCard } from '../modals'
import { normalizePrintingLabel } from '../../utils/variantLabeling'
import { windowEvents } from '../../constants/events'
import { useCurrency } from '../../contexts/CurrencyContext'
import styles from './GlobalCardSearch.module.css'

type HeaderGlobalCardSearchProps = {
  /** The phone's full-screen search: filters and results fill the sheet. */
  inline?: boolean
}

export const HeaderGlobalCardSearch: FC<HeaderGlobalCardSearchProps> = ({ inline = false }) => {
  const navigate = useNavigate()
  const { convertPrice, currency, baseCurrency } = useCurrency()
  const [searchMode, setSearchMode] = useState<'catalog' | 'inventory'>('catalog')
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customGame, setCustomGame] = useState('')
  const [customPrinting, setCustomPrinting] = useState('Normal')
  const [customCondition, setCustomCondition] = useState('Near Mint')
  const [customType, setCustomType] = useState<'card' | 'bundle' | 'box' | 'deck' | 'promo' | 'accessory'>('card')
  const [customQuantity, setCustomQuantity] = useState('1')
  const [customPrice, setCustomPrice] = useState('')
  const [customCostBasis, setCustomCostBasis] = useState('')
  const [customDescription, setCustomDescription] = useState('')
  const [customSubmitting, setCustomSubmitting] = useState(false)
  const [customError, setCustomError] = useState<string | null>(null)
  const [pricingModalCard, setPricingModalCard] = useState<CardPricingModalCard | null>(null)
  const parsedCustomQuantity = parseQuantityDraft(customQuantity)

  const toPricingCard = (selected: CardSearchResult | InventorySearchResult): CardPricingModalCard => {
    if ('cardName' in selected) {
      return {
        id: selected.cardId || selected.id,
        name: selected.cardName,
        setId: selected.setId,
        setName: selected.setName,
        setNumber: selected.setNumber,
        gameName: selected.gameName,
        imageUrl: selected.imageUrl || null,
        variants: [{
          id: `${selected.id}-default`,
          printing: normalizePrintingLabel(selected.printing || ''),
          condition: selected.condition || 'Unspecified',
          priceCents: Number.isFinite(selected.priceCents) ? Math.max(0, Math.round(selected.priceCents)) : 0,
        }],
        defaultPriceCents: Number.isFinite(selected.priceCents) ? Math.max(0, Math.round(selected.priceCents)) : 0,
      }
    }

    return {
      id: selected.id,
      name: selected.name,
      setId: selected.setId,
      setName: selected.setName,
      setCode: selected.setCode,
      setNumber: selected.setNumber,
      gameName: selected.gameName,
      rarity: selected.rarity,
      imageUrl: selected.imageUrl || null,
      variants: Array.isArray(selected.variants)
        ? selected.variants.map((variant, index) => ({
            id: variant.id || `${selected.id}-${index}`,
            printing: normalizePrintingLabel(variant.printing || selected.printing || ''),
            condition: variant.condition || selected.condition || 'Unspecified',
            priceCents: getVariantPriceCents(variant),
          }))
        : [],
      defaultPriceCents: pickBestPriceCents(selected.priceCents),
    }
  }

  const handleSelectCard = (selected: CardSearchResult | InventorySearchResult) => {
    setPricingModalCard(toPricingCard(selected))
  }

  const handleOpenManualAdd = (query: string) => {
    setCustomName((prev) => prev || query.trim())
    setCustomError(null)
    setShowManualAdd(true)
  }

  const handleCloseManualAdd = () => {
    setShowManualAdd(false)
    setCustomError(null)
  }

  const handleSubmitManualAdd = async () => {
    if (!customName.trim()) {
      setCustomError('Card name is required.')
      return
    }

    const priceNum = Number(customPrice)
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setCustomError('Enter a valid manual price.')
      return
    }

    if (parsedCustomQuantity === null) {
      setCustomError(getQuantityRangeWarning())
      return
    }

    const costNum = Number(customCostBasis)
    const displayCostBasisCents = Number.isFinite(costNum) && costNum > 0 ? Math.round(costNum * 100) : undefined

    // User enters prices in their display currency; backend stores USD cents, plus the typed
    // amount, which it lists exactly when it is the store's currency.
    const displayPriceCents = Math.round(priceNum * 100)
    const priceCents = convertPrice(displayPriceCents, currency, baseCurrency)
    const costBasisCents = displayCostBasisCents !== undefined
      ? convertPrice(displayCostBasisCents, currency, baseCurrency)
      : undefined

    try {
      setCustomSubmitting(true)
      setCustomError(null)

      await api.post('/inventory/custom', {
        name: customName.trim(),
        game: customGame.trim(),
        printing: customPrinting.trim() || 'Normal',
        condition: customCondition.trim() || 'Near Mint',
        type: customType,
        quantity: parsedCustomQuantity,
        priceCents,
        priceDisplayCents: displayPriceCents,
        priceDisplayCurrency: currency,
        costBasisCents,
        description: customDescription.trim(),
      })

      window.dispatchEvent(new Event(windowEvents.inventoryUpdated))
      setShowManualAdd(false)
      navigate('/inventory')
    } catch (error: any) {
      setCustomError(error?.response?.data?.message || 'Failed to add manual card.')
    } finally {
      setCustomSubmitting(false)
    }
  }

  return (
    <div className={`${styles.searchContainer} ${inline ? styles.searchContainerInline : ''}`}>
      <div className={styles.searchInputRow}>
        <div className={styles.searchInputFlex}>
          <UnifiedCardSearch
            inline={inline}
            autoFocus={inline}
            onSelectCard={handleSelectCard}
            searchType={searchMode}
            placeholder={searchMode === 'catalog' ? 'Search name, set, number…' : 'Search inventory: name, set, number…'}
            // One box: collector numbers and set codes are just more words (the server matches
            // every word against name, set name/code, number and game).
            showSetNumberInput={false}
            showGameFilter
            showSetFilter={searchMode === 'catalog'}
            variant="setsLike"
            showResultTypeFilter={searchMode === 'catalog'}
            defaultSearchBy="cards"
            allowSetResults={searchMode === 'catalog'}
            showSetTypeOptions={searchMode === 'catalog'}
            clearQueryOnSelect={false}
            catalogVariantPreview={searchMode === 'catalog' ? 'count' : 'interactive'}
            onManualAdd={searchMode === 'catalog' ? handleOpenManualAdd : undefined}
            panelHeader={(
              <div className={styles.scopeSwitch} role="group" aria-label="Search scope">
                {([['catalog', 'All cards'], ['inventory', 'My inventory']] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`${styles.scopeSwitchOption} ${searchMode === value ? styles.scopeSwitchOptionActive : ''}`}
                    onClick={() => setSearchMode(value)}
                    aria-pressed={searchMode === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          />
        </div>
      </div>

      {showManualAdd && (
        <div className={styles.manualAddModalBackdrop} onClick={handleCloseManualAdd}>
          <div className={styles.manualAddModal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.manualAddHeader}>
              <h3>Add Card Manually</h3>
              <button type="button" className={styles.manualAddClose} onClick={handleCloseManualAdd} aria-label="Close">
                ×
              </button>
            </div>

            <div className={styles.manualAddGrid}>
              <label className={styles.manualAddField}>
                Card Name
                <input value={customName} onChange={(e) => setCustomName(e.target.value)} className={styles.manualAddInput} />
              </label>
              <label className={styles.manualAddField}>
                Game
                <input value={customGame} onChange={(e) => setCustomGame(e.target.value)} className={styles.manualAddInput} placeholder="Pokémon" />
              </label>
              <label className={styles.manualAddField}>
                Type
                <select value={customType} onChange={(e) => setCustomType(e.target.value as any)} className={styles.manualAddInput}>
                  <option value="card">Card</option>
                  <option value="bundle">Bundle</option>
                  <option value="box">Box</option>
                  <option value="deck">Deck</option>
                  <option value="promo">Promo</option>
                  <option value="accessory">Accessory</option>
                </select>
              </label>
              <label className={styles.manualAddField}>
                Printing
                <input value={customPrinting} onChange={(e) => setCustomPrinting(e.target.value)} className={styles.manualAddInput} />
              </label>
              <label className={styles.manualAddField}>
                Condition
                <input value={customCondition} onChange={(e) => setCustomCondition(e.target.value)} className={styles.manualAddInput} />
              </label>
              <label className={styles.manualAddField}>
                Quantity
                <input type="number" min={1} step={1} value={customQuantity} onChange={(e) => setCustomQuantity(normalizeQuantityDraft(e.target.value))} className={styles.manualAddInput} />
              </label>
              <label className={styles.manualAddField}>
                Price (required)
                <input type="number" min={0.01} step={0.01} value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} className={styles.manualAddInput} placeholder="4.99" />
              </label>
              <label className={styles.manualAddField}>
                Cost Basis (optional)
                <input type="number" min={0.01} step={0.01} value={customCostBasis} onChange={(e) => setCustomCostBasis(e.target.value)} className={styles.manualAddInput} placeholder="2.50" />
              </label>
            </div>

            <label className={styles.manualAddField}>
              Description (optional)
              <textarea value={customDescription} onChange={(e) => setCustomDescription(e.target.value)} className={styles.manualAddTextarea} rows={2} />
            </label>

            {parsedCustomQuantity === null && customQuantity !== '' && (
              <div className={styles.manualAddError}>{getQuantityRangeWarning()}</div>
            )}

            {customError && <div className={styles.manualAddError}>{customError}</div>}

            <div className={styles.manualAddActions}>
              <button type="button" className={styles.manualAddSecondary} onClick={handleCloseManualAdd} disabled={customSubmitting}>
                Cancel
              </button>
              <button type="button" className={styles.manualAddPrimary} onClick={() => void handleSubmitManualAdd()} disabled={customSubmitting || !customName.trim() || !customPrice || parsedCustomQuantity === null}>
                {customSubmitting ? 'Adding…' : 'Add to Inventory'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CardPricingInsightsModal
        open={Boolean(pricingModalCard)}
        card={pricingModalCard}
        onClose={() => setPricingModalCard(null)}
      />
    </div>
  )
}

export default HeaderGlobalCardSearch
